import React, { FC } from 'react';
import * as Yup from 'yup';

import { Nullable } from 'src/types/nullable.type';

import { useCurrentOrganization } from 'src/store/organization';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { FormikTextarea } from 'src/common/form/formik/textarea';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import { Journey } from 'src/common/interfaces/journey.interface';
import { apiCreateOrganizationTemplateFromJourney } from 'src/utils/journeyApi';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { FreeGatingEnum, useFreeGatingStore } from 'src/common/free-gating/store';

interface JourneyTemplateFormProps {
  journey: Nullable<Journey>;
  onSuccess: (response: any) => void;
}

export const JourneyTemplateForm: FC<JourneyTemplateFormProps> = ({ journey, onSuccess }) => {
  const { currentOrganization } = useCurrentOrganization();
  const { features, showUpgradeModal } = useFeatureRestrictions();
  const setFreeGatingModalByType = useFreeGatingStore((state) => state.setFreeGatingModalByType);
  
  if (!journey) return null;

  // Check if user has access to organization templates
  if (!features.canAccessOrganizationTemplates) {
    setFreeGatingModalByType(FreeGatingEnum.ORG_TEMPLATES, true);
    return null;
  }

  const formikParams = {
    initialValues: {
      journey_id: journey.id,
      name: journey.name,
      description: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Required'),
      description: Yup.string(),
    }),
    onSubmit: async (values: any) => {
      const response = await apiCreateOrganizationTemplateFromJourney(
        currentOrganization.id,
        values.journey_id,
        values.name,
        values.description,
      );

      onSuccess && onSuccess(response);
    },
  };

  return (
    <JourneyFormik {...formikParams}>
      {(formProps: any) => (
        <JourneyForm>
          <div className='flex'>
            <div className='text-bedrock-p'>
              Creating a Template from this Journey will allow your organization members to quickly build new Journeys
              using this as a base. This Journey will remain intact.
            </div>
          </div>
          <div className='flex flex-col space-y-4 mt-lg'>
            <FormikTextInput
              id='template-name'
              name='name'
              required={true}
              type='text'
              placeholder='Acme Corp'
              label='Template name'
            />
            <FormikTextarea
              id='template-description'
              name='description'
              required={false}
              placeholder='Template description'
              label='Template description'
              inputClasses='h-24'
            />
            <button type='submit' className='btn-bedrock-primary' disabled={!formProps.isValid}>
              Create Template
            </button>
          </div>
        </JourneyForm>
      )}
    </JourneyFormik>
  );
};
